import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export type LeaderboardRow = {
  user_id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
  city: string | null;
  country: string | null;
  bio: string | null;
  favorite_genres: string[];
  xp: number;
  level: number;
  rank: number;
  is_premium: boolean;
  owned_count: number;
  followers_count: number;
  covers: string[]; // up to 4 album cover urls
  current_streak: number;
};


export const getLeaderboard = createServerFn({ method: "GET" })
  .inputValidator(
    (i: { q?: string; city?: string; genre?: string; limit?: number } | undefined) =>
      i ?? {},
  )
  .handler(async ({ data }) => {
   try {
    const [{ supabaseAdmin }, { getPremiumUserIds }] = await Promise.all([
      import("@/integrations/supabase/client.server"),
      import("@/lib/premium.server"),
    ]);
    const limit = data.limit ?? 10000;
    const pageSize = 1000;

    const { data: lbRows, error } = await supabaseAdmin
      .from("leaderboard" as any)
      .select("user_id, username, display_name, avatar_url, xp, level, rank, current_streak")
      .order("xp", { ascending: false })
      .limit(limit);
    if (error) throw new Error(error.message);
    const baseRows = ((lbRows ?? []) as unknown) as Array<{
      user_id: string;
      username: string;
      display_name: string | null;
      avatar_url: string | null;
      xp: number;
      level: number;
      rank: number;
      current_streak: number | null;
    }>;
    if (baseRows.length === 0) return [] as LeaderboardRow[];

    const ids = baseRows.map((r) => r.user_id);

    const fetchAllOwnedAlbums = async () => {
      const rows: any[] = [];
      for (let from = 0; ; from += pageSize) {
        const { data: page, error: pageError } = await supabaseAdmin
          .from("user_albums")
          .select("user_id, cover_url, created_at, status")
          .in("user_id", ids)
          .eq("status", "owned")
          .order("created_at", { ascending: false })
          .range(from, from + pageSize - 1);
        if (pageError) throw new Error(pageError.message);
        rows.push(...(page ?? []));
        if (!page || page.length < pageSize) break;
      }
      return rows;
    };

    const fetchAllFollowers = async () => {
      const rows: any[] = [];
      for (let from = 0; ; from += pageSize) {
        const { data: page, error: pageError } = await supabaseAdmin
          .from("follows")
          .select("following_id")
          .in("following_id", ids)
          .range(from, from + pageSize - 1);
        if (pageError) throw new Error(pageError.message);
        rows.push(...(page ?? []));
        if (!page || page.length < pageSize) break;
      }
      return rows;
    };

    const [profilesRes, albumRows, followerRows, premiumIds] = await Promise.all([
      supabaseAdmin
        .from("profiles")
        .select("id, city, country, bio, bio_public, favorite_genres")
        .in("id", ids),
      fetchAllOwnedAlbums(),
      fetchAllFollowers(),
      getPremiumUserIds(ids),
    ]);


    const profilesById = new Map<string, any>();
    (profilesRes.data ?? []).forEach((p: any) => profilesById.set(p.id, p));

    const coversByUser = new Map<string, string[]>();
    const ownedCountByUser = new Map<string, number>();
    albumRows.forEach((a: any) => {
      ownedCountByUser.set(a.user_id, (ownedCountByUser.get(a.user_id) ?? 0) + 1);
      const arr = coversByUser.get(a.user_id) ?? [];
      if (arr.length < 4 && a.cover_url) arr.push(a.cover_url);
      coversByUser.set(a.user_id, arr);
    });

    const followersByUser = new Map<string, number>();
    followerRows.forEach((f: any) => {
      followersByUser.set(f.following_id, (followersByUser.get(f.following_id) ?? 0) + 1);
    });

    let rows: LeaderboardRow[] = baseRows.map((r) => {
      const p = profilesById.get(r.user_id) ?? {};
      return {
        ...r,
        city: p.city ?? null,
        country: p.country ?? null,
        bio: p.bio_public === false ? null : (p.bio ?? null),
        favorite_genres: p.favorite_genres ?? [],
        is_premium: premiumIds.has(r.user_id),
        owned_count: ownedCountByUser.get(r.user_id) ?? 0,
        followers_count: followersByUser.get(r.user_id) ?? 0,
        covers: coversByUser.get(r.user_id) ?? [],
        current_streak: r.current_streak ?? 0,
      };
    });



    if (data.q && data.q.length > 0) {
      const q = data.q.toLowerCase();
      rows = rows.filter(
        (r) =>
          r.username.toLowerCase().includes(q) ||
          (r.display_name ?? "").toLowerCase().includes(q),
      );
    }
    if (data.city && data.city.length > 0) {
      const c = data.city.toLowerCase();
      rows = rows.filter((r) => (r.city ?? "").toLowerCase().includes(c));
    }
    if (data.genre && data.genre.length > 0) {
      const g = data.genre.toLowerCase();
      rows = rows.filter((r) =>
        r.favorite_genres.some((x) => x.toLowerCase().includes(g)),
      );
    }
    return rows;
   } catch (e) {
    console.error("getLeaderboard failed", e);
    return [] as LeaderboardRow[];
   }
  });


export const getLeaderboardCities = createServerFn({ method: "GET" }).handler(async () => {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data } = await supabaseAdmin
    .from("profiles")
    .select("city")
    .not("city", "is", null)
    .limit(500);
  const counts = new Map<string, number>();
  (data ?? []).forEach((r: any) => {
    if (r.city) counts.set(r.city, (counts.get(r.city) ?? 0) + 1);
  });
  return Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 30)
    .map(([city, count]) => ({ city, count }));
});

export const getMyXp = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const since = new Date();
    since.setUTCDate(since.getUTCDate() - 400);
    const [{ data: xpRow }, { data: eventRows }] = await Promise.all([
      context.supabase
        .from("user_xp" as any)
        .select("xp, level")
        .eq("user_id", context.userId)
        .maybeSingle(),
      context.supabase
        .from("xp_events" as any)
        .select("created_at")
        .eq("user_id", context.userId)
        .gte("created_at", since.toISOString())
        .order("created_at", { ascending: false })
        .limit(4000),
    ]);
    const xp = (xpRow as any)?.xp ?? 0;
    const level = (xpRow as any)?.level ?? 1;

    // Per-user streak (the `leaderboard` view is capped at the top 500 rows)
    const ymd = (d: Date) =>
      `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
    const days = new Set<string>();
    for (const row of ((eventRows as any[]) ?? [])) {
      days.add(ymd(new Date(row.created_at)));
    }
    let currentStreak = 0;
    const cursor = new Date();
    if (!days.has(ymd(cursor))) cursor.setUTCDate(cursor.getUTCDate() - 1);
    while (days.has(ymd(cursor))) {
      currentStreak++;
      cursor.setUTCDate(cursor.getUTCDate() - 1);
    }

    const nextLevelXp = (level * level) * 50;
    const prevLevelXp = ((level - 1) * (level - 1)) * 50;
    return { xp, level, nextLevelXp, prevLevelXp, currentStreak };
  });


export type RankedCollector = LeaderboardRow & { rank_delta: number | null };

// Returns leaderboard + rank_delta (positive = moved up since last snapshot).
// A weekly snapshot is taken when the latest snapshot is older than 7 days.
export const getCollectorsRanking = createServerFn({ method: "GET" })
  .inputValidator((i: { limit?: number } | undefined) => i ?? {})
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const rows = (await getLeaderboard({ data: {} } as any)) as LeaderboardRow[];

    // Read the latest snapshot batch
    const { data: latest } = await supabaseAdmin
      .from("leaderboard_snapshots" as any)
      .select("snapshot_at")
      .order("snapshot_at", { ascending: false })
      .limit(1);
    const latestAt: string | null = (latest?.[0] as any)?.snapshot_at ?? null;
    const ageDays = latestAt
      ? (Date.now() - new Date(latestAt).getTime()) / (1000 * 60 * 60 * 24)
      : Infinity;

    let prevRankByUser = new Map<string, number>();
    if (latestAt) {
      const { data: snapRows } = await supabaseAdmin
        .from("leaderboard_snapshots" as any)
        .select("user_id, rank")
        .eq("snapshot_at", latestAt);
      (snapRows ?? []).forEach((r: any) => prevRankByUser.set(r.user_id, r.rank));
    }

    // Take new snapshot if none exists or older than 7 days
    if (!latestAt || ageDays > 7) {
      const now = new Date().toISOString();
      const payload = rows.map((r) => ({
        user_id: r.user_id,
        rank: r.rank,
        xp: r.xp,
        snapshot_at: now,
      }));
      if (payload.length > 0) {
        await supabaseAdmin.from("leaderboard_snapshots" as any).insert(payload);
      }
      // First-ever snapshot → no delta
      if (!latestAt) prevRankByUser = new Map();
    }

    return rows.map((r): RankedCollector => {
      const prev = prevRankByUser.get(r.user_id);
      return {
        ...r,
        rank_delta: prev == null ? null : prev - r.rank,
      };
    });
  });

export const getCollectorSocialProof = createServerFn({ method: "GET" }).handler(
  async () => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { count } = await supabaseAdmin
      .from("profiles")
      .select("*", { count: "exact", head: true });

    const { data: recent } = await supabaseAdmin
      .from("leaderboard" as any)
      .select("user_id, username, display_name, avatar_url")
      .order("xp", { ascending: false })
      .limit(5);

    return {
      count: count ?? 0,
      people: (recent ?? []).map((r: any) => ({
        id: r.user_id,
        username: r.username,
        display_name: r.display_name,
        avatar_url: r.avatar_url,
      })),
    };
  }
);

